import {
  IconButton,
  Grid,
  Tooltip,
  Divider,
  Button,
  Menu,
  MenuItem,
  Fade,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTabs } from '../../../../context/TabContext';
import Iconify from '../../../../reusable-components/iconify';
import OrderTicketRecipients from './OrderTicketRecipients';

TicketHeader.propTypes = {
  handleSetOpenTicket: PropTypes.func,
  currentTicket: PropTypes.number,
  currentTicketMessage: PropTypes.string,
  addBasicTicket: PropTypes.func,
  handleCloseTicket: PropTypes.func,
  subtickets: PropTypes.array,
  mainTicket: PropTypes.object,
  setDisplayedTicket: PropTypes.func,
  displayedTicket: PropTypes.number,
  newUsers: PropTypes.array,
  setNewUsers: PropTypes.func,
  isOrderInfoContainerOpen: PropTypes.bool,
  setIsOrderInfoContainerOpen: PropTypes.func,
  submittingTicket: PropTypes.bool,
};

export default function TicketHeader({
  handleSetOpenTicket,
  currentTicket,
  currentTicketMessage,
  handleCloseTicket,
  subtickets,
  mainTicket,
  setDisplayedTicket,
  displayedTicket,
  newUsers,
  setNewUsers,
  isOrderInfoContainerOpen,
  setIsOrderInfoContainerOpen,
  addBasicTicket,
  submittingTicket,
}) {
  const { addNewTabAndNavigate } = useTabs();
  const [anchorEl, setAnchorEl] = useState(null);
  const isSubticketMenuOpen = Boolean(anchorEl);
  const { user } = useSelector((state) => state.user);

  const users = useMemo(() => {
    if (!mainTicket && !newUsers) return [{ value: `${user?.firstName} ${user?.lastName}`, id: user?.email }];
    if (!mainTicket && Array.isArray(newUsers) && !newUsers.find((u) => u.id === user?.email)) {
      return [...newUsers, { value: `${user?.firstName} ${user?.lastName}`, id: user?.email }];
    }
    return newUsers;
  }, [mainTicket, newUsers, user]);

  useEffect(() => {
    setNewUsers(users);
  }, [users]);

  const iconButtonSx = (colorKey) => ({
    color: (theme) => theme.palette[colorKey].main,
    ':hover': {
      color: (theme) => theme.palette[colorKey].dark,
      backgroundColor: 'transparent',
    },
    padding: 0,
    ml: 0.3,
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);
  const handleOptionClick = (event) => {
    setDisplayedTicket(event.target.value);
    setAnchorEl(null);
  };

  return (
    <>
      <Grid container direction={'row'} justifyContent="space-between">
        <Grid item xs={4}>
          <OrderTicketRecipients newUsers={users || []} setNewUsers={setNewUsers} displayedTicket={displayedTicket} />
        </Grid>
        <Grid item xs={4}>
          {subtickets?.length ? (
            <Button
              sx={{ fontWeight: 'bold', py: 0 }}
              id="fade-button"
              aria-controls={isSubticketMenuOpen ? 'fade-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isSubticketMenuOpen ? 'true' : undefined}
              onClick={handleClick}
              endIcon={isSubticketMenuOpen ? <Iconify icon="mdi:chevron-up" /> : <Iconify icon="mdi:chevron-down" />}
            >
              #{subtickets?.find((s) => s.id === displayedTicket)?.ticketName || displayedTicket}
            </Button>
          ) : mainTicket ? (
            <Typography color="primary">#{displayedTicket}</Typography>
          ) : (
            <Typography color="primary">New Ticket</Typography>
          )}
          <Menu
            id="fade-menu"
            MenuListProps={{
              'aria-labelledby': 'fade-button',
            }}
            anchorEl={anchorEl}
            open={isSubticketMenuOpen}
            onClose={handleClose}
            TransitionComponent={Fade}
          >
            <MenuItem
              onClick={handleOptionClick}
              value={mainTicket?.id}
              sx={(theme) => ({
                backgroundColor: displayedTicket === mainTicket?.id ? theme.palette.primary.lightest : 'inherit',
              })}
            >
              {mainTicket?.id}
            </MenuItem>
            {subtickets?.map((t) => (
              <MenuItem
                onClick={handleOptionClick}
                key={t.id}
                value={t.id}
                sx={(theme) => ({
                  backgroundColor: displayedTicket === t.id ? theme.palette.primary.lightest : 'inherit',
                })}
              >
                {t.ticketName}
              </MenuItem>
            ))}
          </Menu>
        </Grid>
        <Grid item xs={4} sx={{ justifyContent: 'right', display: 'flex' }}>
          {isOrderInfoContainerOpen && (
            <IconButton
              onClick={() => {
                handleSetOpenTicket('open');
                setIsOrderInfoContainerOpen(false);
              }}
              sx={iconButtonSx('info')}
            >
              <Tooltip title="Expand ticket">
                <Iconify icon="eva:expand-fill" width={16} height={16} />
              </Tooltip>
            </IconButton>
          )}
          {(currentTicket !== 0 || addBasicTicket !== null) && (
            <Tooltip title="Open ticket in new tab" placement="top">
              <IconButton
                color="info"
                disabled={submittingTicket && currentTicket === 0}
                onClick={() => {
                  if (currentTicket === 0) addBasicTicket();
                  else {
                    const encodedMessage = encodeURIComponent(currentTicketMessage);
                    addNewTabAndNavigate(`/dashboard/tickets/${currentTicket}?draft=${encodedMessage}`);
                  }
                }}
                sx={iconButtonSx('info')}
              >
                {submittingTicket && currentTicket === 0 ? (
                  <CircularProgress size={16} />
                ) : (
                  <Iconify icon="majesticons:open-line" width={16} height={16} />
                )}
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={handleCloseTicket} sx={{ ...iconButtonSx('info'), transform: 'translateY(-1px)' }}>
            <Iconify icon="ic:sharp-close" width={20} height={20} />
          </IconButton>
        </Grid>
      </Grid>
      <Grid container direction="row" sx={{ mt: users?.length < 3 ? 2.2 : 1.5, mb: 0.5 }}>
        <Divider width="100%" />
      </Grid>
    </>
  );
}
